import * as React from "react";
import ModalPopup from "./modal/ModalPopup";
import { required } from "../model/form/Validators";
import Form from "./form/Form";
import InputState from "../model/form/InputState";
import EmailInput from "./form/input/EmailInput";
import SelectInput from "./form/input/SelectInput";
import Team from "../model/Team";
import TextInput from "./form/input/TextInput";
import sfetch from "../utils/super-fetch";
import SubscriptionPlan from "../model/pricing/SubscriptionPlan";
import Country from "../model/address/Country";
import Option from "../model/Option";

interface InvitationDialogProps {
  title: string;
  submitLabel: string;
  inviteEndpoint: string;
  onCancel: () => void;
  onClose: () => void;

  //optional parameters for specialization
  plans?: SubscriptionPlan[];
  defaultPlanId?: string;
  countries: Country[];
  teams: Team[];
  hasExternalUserId: boolean;
}

interface InvitationDialogState {
  globalErrorMsg?: string;
}

export default class InvitationDialog extends React.Component<
  InvitationDialogProps,
  InvitationDialogState
> {
  static defaultProps = {
    plans: [],
    defaultPlanId: null,
    countries: [],
    teams: [],
    hasExternalUserId: false,
  };

  constructor(props: InvitationDialogProps) {
    super(props);
    this.state = {};
  }

  render() {
    const { title, submitLabel } = this.props;
    return (
      <ModalPopup closeModal={this.props.onCancel}>
        <div className="title">{title}</div>
        <Form
          submitLabel={submitLabel}
          submit={(form) => this.submit(form)}
          generalErrorMsg={this.state.globalErrorMsg}
        >
          <EmailInput
            name="email"
            label={i18n["Global.EmailAddress"]}
            placeholder={i18n["Global.EmailAddressPrompt"]}
            validators={[required()]}
          />

          {this.hasPlan() && (
            <SelectInput
              options={this.props.plans.map(
                (plan) => new Option(plan.id, plan.price / 100 + " " + plan.currency.toUpperCase())
              )}
              name={"plan"}
              label={i18n["Dialog.Invite.PricePlan.Label"]}
              validators={[required()]}
              placeholder={i18n["Dialog.Invite.PricePlan.Prompt"]}
              initialValue={this.props.defaultPlanId}
            />
          )}

          {this.hasCountry() && (
            <SelectInput
              options={this.props.countries.map(
                (country) => new Option(country.name, country.i18n)
              )}
              name={"country"}
              label={i18n["Dialog.Invite.Country.Label"]}
              placeholder={i18n["Dialog.Invite.Country.Prompt"]}
              validators={[required()]}
            />
          )}

          {this.hasTeam() && (
            <SelectInput
              options={this.props.teams.map((team) => new Option(team.uid, team.name))}
              name={"team"}
              label={i18n["Dialog.Invite.Team.Label"]}
              placeholder={i18n["Dialog.Invite.Team.Prompt"]}
              validators={[required()]}
            />
          )}

          {this.hasExternalUserId() && (
            <TextInput
              name="externalUserId"
              label={i18n["Dialog.Invite.ExternalUserId.Name"]}
              placeholder={i18n["Dialog.Invite.ExternalUserId.Prompt"]}
              validators={[required()]}
            />
          )}
        </Form>
      </ModalPopup>
    );
  }

  private submit(form: { [key: string]: InputState }): Promise<void> {
    const payload = {};
    payload["email"] = form["email"].value;
    if (this.hasPlan()) {
      payload["planId"] = form["plan"].value;
    }
    if (this.hasCountry()) {
      payload["country"] = form["country"].value;
    }
    if (this.hasTeam()) {
      payload["teamUid"] = form["team"].value;
    }
    if (this.hasExternalUserId()) {
      payload["externalUserId"] = form["externalUserId"].value;
    }

    const endpoint = this.props.inviteEndpoint;

    return sfetch(endpoint, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(() => this.props.onClose())
      .catch((response) => {
        if (response.status == 400) {
          this.setState({
            globalErrorMsg: i18n["Dialog.GenericInvite.Error.BadRequest"],
          });
        } else if (response.status == 409) {
          this.setState({
            globalErrorMsg: i18n["Dialog.GenericInvite.Error.Conflict"],
          });
        }
        throw response;
      });
  }

  private hasPlan(): boolean {
    return this.props.plans?.length > 0;
  }

  private hasCountry(): boolean {
    return this.props.countries.length > 0;
  }

  private hasTeam(): boolean {
    return this.props.teams.length > 0;
  }

  private hasExternalUserId(): boolean {
    return this.props.hasExternalUserId;
  }
}
